import { useEffect, useMemo, useState } from 'react'
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material'
import { Page } from 'Common/Page/Page'
import { DropDown as DropDownBase, PageHeader, TabPanel } from 'Common/Supplyvue-ui'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DropDown = DropDownBase as any
import { Loader } from 'Common/Loader/Loader'
import { useWarehouseInboundReport } from './WarehouseInboundReport.data'
import { WarehouseInboundMUITable } from './WarehouseInboundReport.MUITable'
import { WarehouseInboundSummaryMUITable } from './WarehouseInboundReport.SummaryMUITable'
import { useOperationalPlan } from 'Features/OperationalPlanner/OperationalPlan.data'

const TABS = {
  DETAIL: 'detail',
  SUMMARY: 'summary',
}

export const WarehouseInboundReportPage = () => {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [selectedTab, setSelectedTab] = useState(TABS.DETAIL)

  const { data: plansData, isFetching: isLoadingPlans } = useOperationalPlan('operational-plans')

  const planOptions: Array<{ key: string; value: string }> = useMemo(() => {
    if (!plansData?.operationalPlanData) return []
    return plansData.operationalPlanData
      .filter(
        (plan: { planId: string; planName: string; status: string }) =>
          plan.planId && plan.planName && plan.status === 'SUCCESS'
      )
      .map((plan: { planId: string; planName: string; status: string }) => ({
        key: plan.planName,
        value: plan.planId,
      }))
  }, [plansData])

  // Auto-select first (latest) plan on load
  useEffect(() => {
    if (planOptions.length > 0 && !selectedPlanId) {
      setSelectedPlanId(planOptions[0].value)
    }
  }, [planOptions, selectedPlanId])

  const {
    data: reportData,
    isFetching: isLoadingReport,
    isError,
  } = useWarehouseInboundReport(selectedPlanId)

  const rows = useMemo(() => {
    if (!reportData?.data) return []
    return reportData.data.map((row, index) => ({ ...row, id: index }))
  }, [reportData])

  const weeks = reportData?.weeks ?? []

  const handlePlanChange = (e: { target: { value: unknown } }) => {
    setSelectedPlanId(e.target.value as string)
  }

  const showEmptyState = !isLoadingReport && !isError && !!selectedPlanId && weeks.length === 0
  const showErrorState = isError && !!selectedPlanId

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Warehouse Inbound Forecast Report" />
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ marginBottom: '20px' }}
        >
          <Box display="flex" sx={{ width: '300px', height: '40px' }}>
            <DropDown
              menuItems={planOptions}
              value={selectedPlanId ?? ''}
              onChange={handlePlanChange}
              label="Plan"
              defaultLabel={isLoadingPlans ? 'Loading plans...' : 'Select Plan'}
              fullWidth
            />
          </Box>
        </Box>

        {isLoadingReport && <Loader />}

        {showErrorState && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography color="error">
              Unable to load data for this plan. The plan may not have report data available.
            </Typography>
          </Box>
        )}

        {showEmptyState && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography color="textSecondary">
              No inbound forecast data available for this plan.
            </Typography>
          </Box>
        )}

        {weeks.length > 0 && (
          <>
            <Box>
              <Paper>
                <Tabs
                  value={selectedTab}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={(_event, newValue) => setSelectedTab(newValue)}
                >
                  <Tab label="Detail" value={TABS.DETAIL} />
                  <Tab label="Summary by Source" value={TABS.SUMMARY} />
                </Tabs>
              </Paper>
            </Box>
            <Box>
              <TabPanel value={selectedTab} index={TABS.DETAIL}>
                <WarehouseInboundMUITable
                  data={rows}
                  weeks={weeks}
                  isLoading={isLoadingReport}
                  apiRefCallback={() => {}}
                />
              </TabPanel>
              <TabPanel value={selectedTab} index={TABS.SUMMARY}>
                <WarehouseInboundSummaryMUITable
                  data={rows}
                  weeks={weeks}
                  isLoading={isLoadingReport}
                  apiRefCallback={() => {}}
                />
              </TabPanel>
            </Box>
          </>
        )}
      </Box>
    </Page>
  )
}
